import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { plansAPI } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useFavourites } from '../context/FavouritesContext';
import { ArrowLeft, Heart, ShareNetwork, MagnifyingGlassPlus, X, Lightning, ShieldCheck, CreditCard } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatCostShort } from '../lib/format';
import { trackViewItem, trackAddToCart } from '../lib/analytics';
import { OptimizedImage } from '../components/plans/OptimizedImage';

const DRAWING_SET_INFO = {
  architectural: {
    label: 'Architectural Drawings',
    description: 'Technical and artistic depiction of a building used to design, plan, and build structures.'
  },
  structural: {
    label: 'Structural Drawings',
    description: 'Technical blueprints prepared by a licensed engineer that outline the load-bearing framework of a building.'
  },
  mechanical: {
    label: 'Mechanical Drawings',
    description: 'Plumbing, HVAC, and mechanical system layouts.'
  },
  electrical: {
    label: 'Electrical Drawings',
    description: 'Electrical wiring, lighting, and outlet layouts.'
  },
  bill_of_quantities: {
    label: 'Bill of Quantities',
    description: 'Detailed financial and material document used in construction that lists itemized quantities, units of measurement, and descriptions of all work and materials needed for the project.'
  },
  local_council: {
    label: 'Local Council Submission',
    description: 'SACAP certified plans, SANS 10400 Form 1 & 2 and all the minimum required documents to submit to any local council.'
  }
};

const FILE_TYPE_DESCRIPTIONS = {
  'PDF': 'View and print your plans on any device.',
  'CAD+PDF': 'Editable. Includes CAD files for modifications by your architect or engineer.'
};

// CAD+PDF markup (kept in sync with backend)
const CAD_UPGRADE = 0.35;

const PlanDetailsPage = () => {
  const { ref: urlRef } = useParams();
  const navigate = useNavigate();
  const { addToCart, savePendingCartItem } = useCart();
  const { isAuthenticated } = useAuth();
  const { isFavourite, toggleFavourite } = useFavourites();
  
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fileType, setFileType] = useState('PDF');
  const [drawingSets, setDrawingSets] = useState(['architectural']);
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [activeImage, setActiveImage] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);

  useEffect(() => {
    fetchPlan();
  }, [urlRef]);

  useEffect(() => {
    if (plan) calculatePrice();
  }, [fileType, drawingSets, plan]);

  const fetchPlan = async () => {
    try {
      const response = await plansAPI.getPlan(urlRef);
      setPlan(response.data);
      // Initialize price with architectural drawing set price
      const archPrice = Number(response.data.drawing_sets?.architectural?.price || response.data.base_price || 0);
      setEstimatedPrice(archPrice);
      // Track view (fire-and-forget)
      plansAPI.trackView(urlRef).catch(() => {});
      trackViewItem({
        reference_number: response.data.reference_number,
        name: response.data.name,
        price: archPrice,
      });
    } catch (error) {
      toast.error('Failed to load plan');
      navigate('/plans');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    if (!plan) return;
    let total = 0;
    const planDs = plan.drawing_sets || {};
    drawingSets.forEach(ds => {
      const cfg = planDs[ds] || {};
      let price = Number(cfg.price || 0);
      // CAD+PDF markup applies ONLY to architectural drawings
      if (fileType === 'CAD+PDF' && ds === 'architectural') {
        price = price * (1 + CAD_UPGRADE);
      }
      total += price;
    });
    setEstimatedPrice(Math.round(total * 100) / 100);
  };

  const toggleDrawingSet = (set) => {
    if (set === 'architectural') return;
    setDrawingSets(prev =>
      prev.includes(set) ? prev.filter(s => s !== set) : [...prev, set]
    );
  };

  const handleAddToCart = async () => {
    const atcPayload = {
      reference_number: plan.reference_number,
      name: plan.name,
      price: estimatedPrice,
    };
    if (!isAuthenticated) {
      savePendingCartItem(plan.plan_id, { file_type: fileType, drawing_sets: drawingSets });
      sessionStorage.setItem('auth_intent', 'from_purchase');
      trackAddToCart(atcPayload);
      toast.success('Sign in to save this plan — your cart will be waiting for you');
      navigate('/login');
      return;
    }
    const success = await addToCart(plan.plan_id, { file_type: fileType, drawing_sets: drawingSets });
    if (success) {
      trackAddToCart(atcPayload);
      navigate('/cart');
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const shareData = {
      title: plan?.name,
      text: `Check out this building plan: ${plan?.name}`,
      url: url
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // AbortError = user cancelled the share sheet; anything else is a real failure.
        if (err && err.name !== 'AbortError') {
          toast.error('Could not share plan');
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Plan link copied to clipboard');
      } catch (err) {
        toast.error('Could not copy link');
      }
    }
  };

  const handleFavourite = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    toggleFavourite(plan.plan_id);
  };

  if (loading) {
    // Skeleton mirrors the real layout (same container + grid + image box) so the
    // page doesn't jump when content arrives.
    return (
      <div className="min-h-screen bg-background py-8" data-testid="plan-details-loading">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <div className="h-6 w-16 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-9 w-44 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="aspect-[4/3] bg-gray-200 border border-border animate-pulse"></div>
              <div className="grid grid-cols-4 gap-3">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="aspect-square bg-gray-200 border border-border animate-pulse"></div>
                ))}
              </div>
            </div>
            <div className="space-y-5">
              <div className="h-9 w-3/4 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-5 w-1/3 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-20 w-full bg-gray-200 rounded animate-pulse"></div>
              <div className="h-14 w-full bg-gray-200 rounded animate-pulse"></div>
              <div className="h-14 w-full bg-gray-200 rounded animate-pulse"></div>
              <div className="h-12 w-full bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) return null;

  const planDrawingSets = plan.drawing_sets || {};
  const availableSets = Object.keys(DRAWING_SET_INFO).filter(k => 
    planDrawingSets[k]?.enabled === true || k === 'architectural'
  );

  const images = plan.images && plan.images.length > 0 ? plan.images : [plan.image_url];
  const favourited = isFavourite(plan.plan_id);

  return (
    <div className="min-h-screen bg-background py-8" data-testid="plan-details-page">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-text_muted hover:text-primary transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </button>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleFavourite}
              className={`flex items-center space-x-2 px-3 py-2 border rounded transition-colors ${favourited ? 'border-red-500 bg-red-50 text-red-500' : 'border-border hover:bg-gray-50'}`}
              data-testid="favourite-btn"
              aria-label="Favourite this plan"
            >
              <Heart size={20} weight={favourited ? 'fill' : 'regular'} />
              <span className="text-sm hidden sm:inline">{favourited ? 'Favourited' : 'Favourite'}</span>
            </button>
            <button
              onClick={handleShare}
              className="flex items-center space-x-2 px-3 py-2 border border-border rounded hover:bg-gray-50 transition-colors"
              data-testid="share-btn"
              aria-label="Share this plan"
            >
              <ShareNetwork size={20} />
              <span className="text-sm hidden sm:inline">Share</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Images */}
          <div className="space-y-4">
            <div className="relative aspect-[4/3] overflow-hidden border border-border bg-white group">
              <OptimizedImage
                src={images[activeImage]}
                alt={plan.name}
                className="w-full h-full object-cover"
                widths={[640, 1024]}
                sizes="(max-width: 1024px) 100vw, 50vw"
                width={1024}
                height={768}
                priority
              />
              <button
                onClick={() => setZoomOpen(true)}
                className="absolute top-3 right-3 p-2 bg-white/95 border border-border rounded hover:bg-white transition-colors"
                data-testid="zoom-btn"
                aria-label="Zoom image"
              >
                <MagnifyingGlassPlus size={22} />
              </button>
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`aspect-square overflow-hidden border-2 bg-white transition-colors ${activeImage === i ? 'border-accent' : 'border-border hover:border-primary'}`}
                    data-testid={`thumbnail-${i}`}
                  >
                    <OptimizedImage
                      src={img}
                      alt={`${plan.name} ${i + 1}`}
                      className="w-full h-full object-cover"
                      widths={[160, 320]}
                      sizes="(max-width: 1024px) 25vw, 150px"
                      width={160}
                      height={160}
                    />
                  </button>
                ))}
              </div>
            )}
            
            {/* Trust Badges (desktop position - below gallery). Mobile shows this below Add-to-Cart instead. */}
            <div className="hidden md:grid grid-cols-3 gap-2 pt-2" data-testid="plan-trust-badges">
              <div className="flex flex-col items-center text-center border border-border p-3 bg-white">
                <Lightning size={22} weight="duotone" className="text-accent mb-1" />
                <span className="text-[10px] font-bold text-primary uppercase" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Instant Digital Delivery</span>
              </div>
              <div className="flex flex-col items-center text-center border border-border p-3 bg-white">
                <ShieldCheck size={22} weight="duotone" className="text-accent mb-1" />
                <span className="text-[10px] font-bold text-primary uppercase" style={{ fontFamily: 'JetBrains Mono, monospace' }}>SA NBR Compliant</span>
              </div>
              <div className="flex flex-col items-center text-center border border-border p-3 bg-white">
                <CreditCard size={22} weight="duotone" className="text-accent mb-1" />
                <span className="text-[10px] font-bold text-primary uppercase" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Multiple Payment Options</span>
              </div>
            </div>
          </div>

          {/* Details & Configuration */}
          <div className="bg-white border border-border p-8 sticky top-24 h-fit">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl font-black text-primary" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {plan.name}
                </h1>
                {plan.reference_number && (
                  <div className="text-xs font-mono text-text_muted mt-1 uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    Ref: {plan.reference_number}
                  </div>
                )}
              </div>
              <span
                className="text-sm font-mono bg-gray-100 px-3 py-1 rounded ml-3 flex-shrink-0"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                Type {plan.plan_type}
              </span>
            </div>

            {/* Key Specifications - Stand size first, Area last (per client request) */}
            <div className="grid grid-cols-3 gap-2 mb-4" data-testid="key-specs">
              <div className="text-center border border-border p-2">
                <div className="text-lg font-bold text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {plan.min_stand_size_sqm > 0 ? `${plan.min_stand_size_sqm}m²` : '—'}
                </div>
                <div className="text-[10px] text-text_muted mt-1 uppercase">Stand</div>
              </div>
              <div className="text-center border border-border p-2">
                <div className="text-lg font-bold text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {plan.bedrooms}
                </div>
                <div className="text-[10px] text-text_muted mt-1 uppercase">Beds</div>
              </div>
              <div className="text-center border border-border p-2">
                <div className="text-lg font-bold text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {plan.bathrooms || 1}
                </div>
                <div className="text-[10px] text-text_muted mt-1 uppercase">Baths</div>
              </div>
              <div className="text-center border border-border p-2">
                <div className="text-lg font-bold text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {plan.floors}
                </div>
                <div className="text-[10px] text-text_muted mt-1 uppercase">Floors</div>
              </div>
              <div className="text-center border border-border p-2">
                <div className="text-lg font-bold text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {formatCostShort(plan.estimated_construction_cost)}
                </div>
                <div className="text-[10px] text-text_muted mt-1 uppercase">Est. Cost</div>
              </div>
              <div className="text-center border border-border p-2">
                <div className="text-lg font-bold text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {plan.area_sqm}m²
                </div>
                <div className="text-[10px] text-text_muted mt-1 uppercase">Area</div>
              </div>
            </div>

            {/* Static "Starting from" price */}
            <div className="flex items-baseline justify-between mb-4 pb-4 border-b border-border" data-testid="starting-from-price">
              <span className="text-xs text-text_muted uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Starting from</span>
              <div className="text-2xl font-bold text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                R{Number(plan.base_price || 0).toFixed(2)}
              </div>
            </div>

            <p className="text-text_muted mb-6">{plan.description}</p>

            {/* File Type Selection */}
            <div className="mb-6">
              <h3 className="text-sm font-bold text-primary mb-3">File Format</h3>
              <div className="grid grid-cols-1 gap-2">
                {['PDF', 'CAD+PDF'].map(type => (
                  <label
                    key={type}
                    className="flex items-start p-3 border-2 cursor-pointer hover:border-accent transition-colors"
                    style={{ borderColor: fileType === type ? '#FF4F00' : '#E5E5E5' }}
                    data-testid={`file-type-${type.toLowerCase().replace('+', '-')}`}
                  >
                    <input
                      type="radio"
                      name="fileType"
                      value={type}
                      checked={fileType === type}
                      onChange={(e) => setFileType(e.target.value)}
                      className="mr-3 mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{type}</div>
                      <div className="text-xs text-text_muted mt-0.5">{FILE_TYPE_DESCRIPTIONS[type]}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Drawing Sets Selection */}
            <div className="mb-6">
              <h3 className="text-sm font-bold text-primary mb-3">Drawing Sets</h3>
              <div className="space-y-2">
                {availableSets.map(setKey => {
                  const isRequired = setKey === 'architectural';
                  const isSelected = drawingSets.includes(setKey);
                  const info = DRAWING_SET_INFO[setKey];
                  const price = Number(planDrawingSets[setKey]?.price || 0);
                  return (
                    <label
                      key={setKey}
                      className="flex items-start p-3 border cursor-pointer hover:border-accent transition-colors"
                      style={{ borderColor: isSelected ? '#FF4F00' : '#E5E5E5' }}
                      data-testid={`drawing-set-${setKey}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleDrawingSet(setKey)}
                        disabled={isRequired}
                        className="mr-3 mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-medium text-sm">
                            {info.label}
                            {isRequired && <span className="text-accent ml-2 text-xs">(Included)</span>}
                          </div>
                          {price > 0 && (
                            <span className="text-sm font-bold text-accent flex-shrink-0" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              R{price.toFixed(2)}
                            </span>
                          )}
                        </div>
                        {isRequired && fileType === 'CAD+PDF' && price > 0 && (
                          <div className="flex items-center justify-between gap-2 mt-1" data-testid="cad-upgrade-line">
                            <span className="text-xs text-text_muted">CAD+PDF upgrade</span>
                            <span className="text-sm font-bold text-accent flex-shrink-0" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              R{(price * CAD_UPGRADE).toFixed(2)}
                            </span>
                          </div>
                        )}
                        <div className="text-xs text-text_muted mt-1">{info.description}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Price & Add to Cart */}
            <div className="pt-6 border-t border-border">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-text_muted">Total Price:</span>
                <div className="text-3xl font-bold text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  R{estimatedPrice.toFixed(2)}
                </div>
              </div>
              <button
                onClick={handleAddToCart}
                className="w-full bg-primary text-white py-4 rounded font-bold hover:bg-opacity-90 transition-colors"
                data-testid="add-to-cart-btn"
              >
                Add to Cart
              </button>

              {/* Trust Badges - MOBILE ONLY, positioned just below Add-to-Cart */}
              <div className="grid grid-cols-3 gap-2 pt-4 md:hidden" data-testid="plan-trust-badges-mobile">
                <div className="flex flex-col items-center text-center border border-border p-3 bg-white">
                  <Lightning size={22} weight="duotone" className="text-accent mb-1" />
                  <span className="text-[10px] font-bold text-primary uppercase" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Instant Digital Delivery</span>
                </div>
                <div className="flex flex-col items-center text-center border border-border p-3 bg-white">
                  <ShieldCheck size={22} weight="duotone" className="text-accent mb-1" />
                  <span className="text-[10px] font-bold text-primary uppercase" style={{ fontFamily: 'JetBrains Mono, monospace' }}>SA NBR Compliant</span>
                </div>
                <div className="flex flex-col items-center text-center border border-border p-3 bg-white">
                  <CreditCard size={22} weight="duotone" className="text-accent mb-1" />
                  <span className="text-[10px] font-bold text-primary uppercase" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Multiple Payment Options</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Zoom Modal */}
      {zoomOpen && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setZoomOpen(false)}
          data-testid="zoom-modal"
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors z-10"
            onClick={() => setZoomOpen(false)}
            data-testid="close-zoom-btn"
          >
            <X size={24} />
          </button>
          <img
            src={images[activeImage]}
            alt={plan.name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default PlanDetailsPage;
